import { useMemo } from "react";
import styles from "./Field.module.css";
import { Field } from "./Field";

export interface DateRange {
  /** ISO 8601 date string (YYYY-MM-DD). null = open range. */
  start: string | null;
  end: string | null;
}

interface DateRangePickerProps {
  label: string;
  value: DateRange;
  onChange: (next: DateRange) => void;
  error?: string;
  helper?: string;
  disabled?: boolean;
}

/**
 * DateRangePicker — two native date inputs.
 *
 * Building a first-class calendar UI is Phase-2.5 work (localization,
 * keyboard nav, screen-reader announcements, disabled ranges).
 * A native <input type="date"> pair is what we ship for now; it works
 * on every browser, respects the OS locale, and cannot be exploited.
 *
 * Same field styling as <Field>. The two dates render side-by-side.
 * Cross-field validation (end before start) is the caller's job — the
 * component doesn't enforce it because "one date only" is a legitimate
 * use case for a partial range.
 */
export function DateRangePicker({
  label,
  value,
  onChange,
  error,
  helper,
  disabled,
}: DateRangePickerProps) {
  const startVal = value.start ?? "";
  const endVal = value.end ?? "";
  const combinedHelper = useMemo(() => {
    if (helper) return helper;
    if (value.start && value.end) return `${value.start} → ${value.end}`;
    if (value.start) return `From ${value.start}`;
    if (value.end) return `Through ${value.end}`;
    return "Any date";
  }, [helper, value]);

  return (
    <div className={styles.field}>
      <span className={styles.label}>{label}</span>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <Field
          label=""
          value={startVal}
          onChange={(s) => onChange({ ...value, start: s || null })}
          type="text"
          inputMode="numeric"
          placeholder="YYYY-MM-DD"
          disabled={disabled}
        />
        <Field
          label=""
          value={endVal}
          onChange={(e) => onChange({ ...value, end: e || null })}
          type="text"
          inputMode="numeric"
          placeholder="YYYY-MM-DD"
          disabled={disabled}
        />
      </div>
      <div className={styles.footer}>
        {error ? (
          <span className={styles.errorText} role="alert">
            {error}
          </span>
        ) : (
          <span className={styles.helper}>{combinedHelper}</span>
        )}
      </div>
    </div>
  );
}
